/**
 * How big a clinic is, as a value — no store, no network, no React.
 *
 * `navigation.ts` is deliberately pure so the sidebar can be answered without
 * mounting the app (see its header), and it needs `shapeAtLeast`. Keeping the
 * type and comparison here, apart from the store that fetches them, is what
 * stops an axios client being dragged into a module whose whole point is that
 * it has no dependencies.
 *
 * Derived on the backend from members, providers and branches — never asked.
 * Two rules make deriving safe, and the store enforces both:
 *
 *   Grow only.  A row that has appeared for a clinic never disappears again.
 *               Suspending a receptionist must not silently remove Inbox from
 *               the owner's nav; that reads as a bug, not as tidiness.
 *
 *   Hide only.  Shape can remove a row; it can never add one. Permissions and
 *               module toggles still decide what a person may reach, and
 *               everything hidden stays reachable from ⌘K and /explore.
 */

export type ClinicShape = 'solo' | 'practice' | 'clinic' | 'group';

export const CLINIC_SHAPES: ClinicShape[] = [
  'solo',
  'practice',
  'clinic',
  'group',
];

export const shapeRank = (shape: ClinicShape): number =>
  CLINIC_SHAPES.indexOf(shape);

/** True when a clinic of `shape` is at least as large as `required`. */
export const shapeAtLeast = (
  shape: ClinicShape,
  required: ClinicShape,
): boolean => shapeRank(shape) >= shapeRank(required);
